import { Button, Grid, Typography } from '@mui/material';
import { ReaderFirmwareUpdateStatus } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import SectionPaper from 'v2/components/SectionPaper/SectionPaper';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useCloudControllers from 'v2/hooks/useCloudControllers/useCloudControllers';
import useReaderFirmwareUpdate from 'v2/hooks/useReaderFirmwareUpdate/useReaderFirmwareUpdate';

interface Props {
  deviceId: string;
}

const DeviceDebug: React.FC<Props> = ({ deviceId }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (msg: string) => showSnackbar(msg, { severity: 'error' });
  const { openDoor, openingDoor, identifyReader, identifyingReader } = useCloudControllers({
    translation,
    handleFetchError
  });
  const { updateReaderFirmware, requestingReaderFirmwareUpdate, firmwareUpdate, isFirmwareUpdateInProgress } =
    useReaderFirmwareUpdate({
      doorId: deviceId,
      translation,
      handleFetchError,
      onFinished: (status) =>
        status === ReaderFirmwareUpdateStatus.Succeeded
          ? showSnackbar(translation.update_reader_firmware_succeeded, { severity: 'success' })
          : showSnackbar(translation.update_reader_firmware_failed, { severity: 'error' })
    });

  const handleOpenDoor = async (doorId: string) => {
    const success = await openDoor(doorId);
    if (success) {
      showSnackbar(translation.open_door_sent, { severity: 'success' });
    }
  };

  const handleIdentifyReader = async (doorId: string) => {
    const success = await identifyReader(doorId);
    if (success) {
      showSnackbar(translation.identify_reader_sent, { severity: 'success' });
    }
  };

  const handleUpdateReaderFirmware = async () => {
    const success = await updateReaderFirmware();
    if (success) {
      showSnackbar(translation.update_reader_firmware_sent, { severity: 'success' });
    }
  };

  const firmwareStatusLabels: Record<ReaderFirmwareUpdateStatus, string> = {
    [ReaderFirmwareUpdateStatus.Queued]: translation.reader_firmware_queued,
    [ReaderFirmwareUpdateStatus.Downloading]: translation.reader_firmware_downloading,
    [ReaderFirmwareUpdateStatus.Installing]: translation.reader_firmware_installing,
    [ReaderFirmwareUpdateStatus.Succeeded]: translation.reader_firmware_succeeded,
    [ReaderFirmwareUpdateStatus.Failed]: translation.reader_firmware_failed,
    [ReaderFirmwareUpdateStatus.Rejected]: translation.reader_firmware_rejected,
    [ReaderFirmwareUpdateStatus.TimedOut]: translation.reader_firmware_timed_out,
    [ReaderFirmwareUpdateStatus.Canceled]: translation.reader_firmware_canceled,
    [ReaderFirmwareUpdateStatus.Removed]: translation.reader_firmware_removed
  };

  const getFirmwareButtonLabel = () => {
    if (!firmwareUpdate) return translation.update_reader_firmware;
    const statusLabel = firmwareStatusLabels[firmwareUpdate.status];
    if (!isFirmwareUpdateInProgress)
      return translation.update_reader_firmware_last_result.replace('{{status}}', statusLabel);
    if (firmwareUpdate.status === ReaderFirmwareUpdateStatus.Queued) return statusLabel;
    return `${statusLabel} ${firmwareUpdate.percentage}%`;
  };

  return (
    <SectionPaper>
      <Grid container direction="column" spacing={2}>
        <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
          <Typography variant="h5">Debug</Typography>
        </Grid>
        <Grid container spacing={1}>
          <Button
            id={`open-door-button`}
            onClick={() => handleOpenDoor(deviceId)}
            variant="outlined"
            sx={{ alignSelf: 'center' }}
            loading={openingDoor}
          >
            {translation.open_door}
          </Button>
          <Button
            id={`identify-reader-button`}
            onClick={() => handleIdentifyReader(deviceId)}
            variant="outlined"
            sx={{ alignSelf: 'center' }}
            loading={identifyingReader}
          >
            {translation.identify_reader}
          </Button>
          <Button
            id={`update-reader-firmware-button`}
            onClick={handleUpdateReaderFirmware}
            variant="outlined"
            sx={{ alignSelf: 'center' }}
            loading={requestingReaderFirmwareUpdate}
            disabled={isFirmwareUpdateInProgress}
          >
            {getFirmwareButtonLabel()}
          </Button>
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default DeviceDebug;
